import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import { fetchOrganisations, deleteOrganisation } from '../../../store/slices/organizationSlice';
import { AppDispatch } from '../../../store';
import OrganizationsTable from '../../../components/organisations/OrganisationsTable';
import { useOrganizations } from '../../../hooks';
import SuperAdminLayout from '../../../components/layout/SuperAdmin/SuperAdminLayout';
import ConfirmationDialog from '../../../components/common/ConfirmationDialog';
import { organizationEndpoints } from '../../../services/api/endpoints/organisation.endpoint';
import { NotificationT, SEVERITY } from '../../../types/notification';
import Notifications from '../../../components/common/Notifications';

const PendingRequestsView = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { organizations, isLoading, error } = useOrganizations();
  const [isDelete, setIsDelete] = useState(false);

  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    orgId: '',
  });

  const [notification, setNotification] = useState<NotificationT>({
    open: false,
    message: '',
    severity: SEVERITY.SUCCESS,
  });

  useEffect(() => {
    dispatch(fetchOrganisations());
  }, [dispatch]);

  // Filter pending organizations
  //   const pendingOrganizations = organizations.filter(org => org.status !== 'active');
  const pendingOrganizations = organizations
    ? organizations.filter(org => org.status !== 'active')
    : [];

  const handleDelete = (id: string) => {
    setConfirmDialog({
      open: true,
      orgId: id,
    });
  };

  const handleDeleteConfirm = async () => {
    setIsDelete(true);
    const deletedOrganisations = await organizationEndpoints.deleteOrganisation(
      confirmDialog.orgId
    );

    dispatch(deleteOrganisation(confirmDialog.orgId));
    setConfirmDialog({ open: false, orgId: '' });
    setIsDelete(false);
    setNotification({
      open: true,
      message: 'Organisation deleted successfully',
      severity: SEVERITY.SUCCESS,
    });

    return deletedOrganisations;
  };

  if (isLoading) {
    return (
      <SuperAdminLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      </SuperAdminLayout>
    );
  }

  if (error) {
    return (
      <SuperAdminLayout>
        <Box sx={{ p: 3 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      </SuperAdminLayout>
    );
  }

  return (
    <SuperAdminLayout>
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 500, mb: 3 }}>
          Pending Organizations
        </Typography>
        {isDelete ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%', // or specific height if needed
              width: '100%',
            }}
          >
            <CircularProgress size={50} thickness={4} className="text-purple-500 mb-4" />
          </Box>
        ) : (
          <>
            <OrganizationsTable organizations={pendingOrganizations} onDelete={handleDelete} />

            <ConfirmationDialog
              open={confirmDialog.open}
              title="Delete Organisation"
              message="Are you sure you want to delete this organisation? This action cannot be undone."
              onConfirm={handleDeleteConfirm}
              onCancel={() => setConfirmDialog({ open: false, orgId: '' })}
            />
          </>
        )}

        <Notifications
          open={notification.open}
          message={notification.message}
          severity={notification.severity}
          onClose={() => setNotification(prev => ({ ...prev, open: false }))}
        />
      </Box>
    </SuperAdminLayout>
  );
};

export default PendingRequestsView;
